/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, clearCart, setCartItems } from "context";
import fetchWithAuth from "layouts/tables/data/api";

import appSettings from "appsettings.json";

function ProtectedRoute({ children }) {
  const token = localStorage.getItem("token");
  const [isTocAccepted, setIsTocAccepted] = useState(
    localStorage.getItem("isTocAccepted") === "true"
  );
  const [isCheckingToc, setIsCheckingToc] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let isCancelled = false;

    const syncTocStatus = async () => {
      if (!token) {
        if (!isCancelled) {
          setIsCheckingToc(false);
        }
        return;
      }

      try {
        const response = await fetch(`${appSettings.baseUrl}${appSettings.endpoints.tocStatus}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const accepted = data?.isTocAccepted === true;
          localStorage.setItem("isTocAccepted", accepted ? "true" : "false");
          if (!isCancelled) {
            setIsTocAccepted(accepted);
          }
        } else if (response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("isTocAccepted");
          if (!isCancelled) {
            setIsTocAccepted(false);
          }
        }
      } catch (error) {
        // Keep current local state when TOC status endpoint is temporarily unavailable.
      } finally {
        if (!isCancelled) {
          setIsCheckingToc(false);
        }
      }
    };

    syncTocStatus();

    return () => {
      isCancelled = true;
    };
  }, [token, location.pathname]);

  if (!token) {
    return <Navigate to="/authentication/mobile-sign-in" />;
  }
  if (isCheckingToc) {
    return null;
  }
  if (!isTocAccepted && location.pathname !== "/authentication/terms-and-conditions") {
    return <Navigate to="/authentication/terms-and-conditions" />;
  }
  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { direction, darkMode, cartItems } = controller;
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    let isCancelled = false;
    const token = localStorage.getItem("token");

    if (!token) {
      clearCart(dispatch);
      return undefined;
    }

    if (cartItems.length > 0) {
      return undefined;
    }

    const loadCartItems = async () => {
      try {
        const response = await fetchWithAuth(
          `${appSettings.baseUrl}${appSettings.endpoints.getCartItems}`,
          { method: "GET" }
        );

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const toAbsoluteImageUrl = (rawUrl) => {
          if (!rawUrl) return "";
          if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) return rawUrl;
          const normalizedPath = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
          return `${appSettings.baseUrl}${normalizedPath}`;
        };

        const mappedItems = Array.isArray(data)
          ? data.map((item) => ({
              id: Number(item.listingId),
              listingId: Number(item.listingId),
              name: item.title || "",
              size: "",
              image:
                Array.isArray(item.imageUrls) && item.imageUrls.length > 0
                  ? toAbsoluteImageUrl(item.imageUrls[0])
                  : "",
              price: Number(item.price) || 0,
              quantity: Number(item.quantity) || 1,
            }))
          : [];

        if (!isCancelled) {
          setCartItems(dispatch, mappedItems);
        }
      } catch {
        // Ignore startup cart hydration errors and keep current cart state.
      }
    };

    loadCartItems();

    return () => {
      isCancelled = true;
    };
  }, [dispatch, pathname, cartItems.length]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        const isPublicRoute = route.public === true || route.route.startsWith("/authentication");
        const element = isPublicRoute ? (
          route.component
        ) : (
          <ProtectedRoute>{route.component}</ProtectedRoute>
        );
        return <Route exact path={route.route} element={element} key={route.key} />;
      }

      return null;
    });

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </ThemeProvider>
  );
}
